import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import {
  DocumentIcon,
  FolderIcon,
  ArrowPathIcon,
  PencilIcon,
  ClipboardDocumentIcon,
  CameraIcon,
  EyeIcon,
  PrinterIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import ToggleSwitch from '../components/ToggleSwitch';
import BulkUpdateModal from '../components/AdminPolicySettings/BulkUpdateModal';

type TabType = 'files' | 'folders';

interface FilePermissionVisibility {
  convert_original: boolean;
  save_changes: boolean;
  copy_paste: boolean;
  screen_capture: boolean;
  screen_watermark: boolean;
  print: boolean;
  print_watermark: boolean;
  share: boolean;
}

interface FolderPermissionVisibility {
  create: boolean;
  copy: boolean;
  add: boolean;
  move: boolean;
  share: boolean;
  delete: boolean;
}

const STORAGE_KEY = 'admin_policy_settings_tab';

const AdminPolicySettings: React.FC = () => {
  const { t } = useTranslation('adminPolicy');
  usePageTitle(t('title'));
  const { user } = useAuth();
  const { setOnSave, setIsSubmitting } = useUnsavedChanges();
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    // Load saved tab from localStorage, default to 'files'
    const savedTab = localStorage.getItem(STORAGE_KEY);
    return (savedTab === 'files' || savedTab === 'folders') ? savedTab as TabType : 'files';
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<{
    file_permissions?: Partial<FilePermissionVisibility>;
    folder_permissions?: Partial<FolderPermissionVisibility>;
  } | null>(null);

  // File permissions state (default to true - visible)
  const [filePermissions, setFilePermissions] = useState<FilePermissionVisibility>({
    convert_original: true,
    save_changes: true,
    copy_paste: true,
    screen_capture: true,
    screen_watermark: true,
    print: true,
    print_watermark: true,
    share: true,
  });

  // Folder permissions state (default to true - visible)
  const [folderPermissions, setFolderPermissions] = useState<FolderPermissionVisibility>({
    create: true,
    copy: true,
    add: true,
    move: true,
    share: true,
    delete: true,
  });

  // Save tab selection to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, activeTab);
  }, [activeTab]);

  // Load policy settings
  useEffect(() => {
    const loadData = async () => {
      if (!user?.company_id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const settings = await apiService.getCompanyPolicySettings(user.company_id);

        // Set file permissions (default to true if not set)
        if (settings.file_permissions) {
          setFilePermissions({
            convert_original: settings.file_permissions.convert_original ?? true,
            save_changes: settings.file_permissions.save_changes ?? true,
            copy_paste: settings.file_permissions.copy_paste ?? true,
            screen_capture: settings.file_permissions.screen_capture ?? true,
            screen_watermark: settings.file_permissions.screen_watermark ?? true,
            print: settings.file_permissions.print ?? true,
            print_watermark: settings.file_permissions.print_watermark ?? true,
            share: settings.file_permissions.share ?? true,
          });
        }

        // Set folder permissions (default to true if not set)
        if (settings.folder_permissions) {
          setFolderPermissions({
            create: settings.folder_permissions.create ?? true,
            copy: settings.folder_permissions.copy ?? true,
            add: settings.folder_permissions.add ?? true,
            move: settings.folder_permissions.move ?? true,
            share: settings.folder_permissions.share ?? true,
            delete: settings.folder_permissions.delete ?? true,
          });
        }
      } catch (err: any) {
        console.error('Failed to load policy settings:', err);
        setError(t('errors.loadFailed'));
        toast.error(t('toast.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user?.company_id, t]);

  // Use refs to store the latest state so save handlers don't need them in dependencies
  const filePermissionsRef = useRef(filePermissions);
  const folderPermissionsRef = useRef(folderPermissions);

  useEffect(() => {
    filePermissionsRef.current = filePermissions;
  }, [filePermissions]);

  useEffect(() => {
    folderPermissionsRef.current = folderPermissions;
  }, [folderPermissions]);

  // Track if we're currently saving to prevent auto-saves
  const isSavingRef = useRef(false);


  const handleSave = useCallback(async (applyToExisting: boolean = false) => {
    if (!user?.company_id || isSavingRef.current) return;

    try {
      isSavingRef.current = true;
      setIsSubmitting(true);
      setError(null);

      const settingsToSave: {
        file_permissions?: FilePermissionVisibility;
        folder_permissions?: FolderPermissionVisibility;
      } = {};

      // Only include the active tab's permissions in the update
      // Use current state directly instead of ref to ensure we have the latest value
      if (activeTab === 'files') {
        settingsToSave.file_permissions = filePermissionsRef.current;
      } else {
        settingsToSave.folder_permissions = folderPermissionsRef.current;
      }

      await apiService.updateCompanyPolicySettings(
        user.company_id,
        settingsToSave,
        applyToExisting
      );

      // Reload data from server to ensure we have the latest persisted state
      const freshSettings = await apiService.getCompanyPolicySettings(user.company_id);
      
      // Update state with fresh data from server
      if (freshSettings.file_permissions) {
        const updatedFilePerms: FilePermissionVisibility = {
          convert_original: freshSettings.file_permissions.convert_original ?? true,
          save_changes: freshSettings.file_permissions.save_changes ?? true,
          copy_paste: freshSettings.file_permissions.copy_paste ?? true,
          screen_capture: freshSettings.file_permissions.screen_capture ?? true,
          screen_watermark: freshSettings.file_permissions.screen_watermark ?? true,
          print: freshSettings.file_permissions.print ?? true,
          print_watermark: freshSettings.file_permissions.print_watermark ?? true,
          share: freshSettings.file_permissions.share ?? true,
        };
        setFilePermissions(updatedFilePerms);
        filePermissionsRef.current = updatedFilePerms;
      }
      if (freshSettings.folder_permissions) {
        const updatedFolderPerms = {
          create: freshSettings.folder_permissions.create ?? true,
          copy: freshSettings.folder_permissions.copy ?? true,
          add: freshSettings.folder_permissions.add ?? true,
          move: freshSettings.folder_permissions.move ?? true,
          share: freshSettings.folder_permissions.share ?? true,
          delete: freshSettings.folder_permissions.delete ?? true,
        };
        setFolderPermissions(updatedFolderPerms);
        folderPermissionsRef.current = updatedFolderPerms;
      }

      toast.success(t('toast.saveSuccess'));
    } catch (err: any) {
      console.error('Failed to save policy settings:', err);
      setError(t('errors.saveFailed'));
      toast.error(err.response?.data?.error || t('toast.saveFailed'));
    } finally {
      setIsSubmitting(false);
      isSavingRef.current = false;
      setShowBulkUpdateModal(false);
      setPendingChanges(null);
    }
  }, [user?.company_id, activeTab, setIsSubmitting, setError, t]);

  // Track whether we're enabling or disabling
  const [isEnabling, setIsEnabling] = useState(false);

  // Handle permission toggle
  const handleToggle = useCallback((permission: string, value: boolean) => {
    if (activeTab === 'files') {
      const oldValue = filePermissions[permission as keyof FilePermissionVisibility];
      const normalizedOldValue = oldValue ?? true;
      
      // Check if we're changing a permission (going from true to false or false to true)
      if ((normalizedOldValue === true && value === false) || (normalizedOldValue === false && value === true)) {
        // Show modal first, don't update state yet
        setIsEnabling(value === true);
        setPendingChanges({ file_permissions: { [permission]: value } });
        setShowBulkUpdateModal(true);
      } else {
        // For other changes, update immediately
        setFilePermissions((prev) => ({ ...prev, [permission]: value }));
      }
    } else {
      const oldValue = folderPermissions[permission as keyof FolderPermissionVisibility];
      
      // Check if we're changing a permission (going from true to false or false to true)
      if ((oldValue === true && value === false) || (oldValue === false && value === true)) {
        // Show modal first, don't update state yet
        setIsEnabling(value === true);
        setPendingChanges({ folder_permissions: { [permission]: value } });
        setShowBulkUpdateModal(true);
      } else {
        // For other changes, update immediately
        setFolderPermissions((prev) => ({ ...prev, [permission]: value }));
      }
    }
  }, [activeTab, filePermissions, folderPermissions]);

  // Set up save handler for unsaved changes context
  useEffect(() => {
    setOnSave(() => handleSave);
    return () => setOnSave(null);
  }, [setOnSave, handleSave]);

  // File permission items
  const filePermissionItems = [
    { key: 'convert_original', labelKey: 'filePermissions.convert_original', icon: ArrowPathIcon },
    { key: 'save_changes', labelKey: 'filePermissions.save_changes', icon: PencilIcon },
    { key: 'copy_paste', labelKey: 'filePermissions.copy_paste', icon: ClipboardDocumentIcon },
    { key: 'screen_capture', labelKey: 'filePermissions.screen_capture', icon: CameraIcon },
    { key: 'screen_watermark', labelKey: 'filePermissions.screen_watermark', icon: EyeIcon },
    { key: 'print', labelKey: 'filePermissions.print', icon: PrinterIcon },
    { key: 'print_watermark', labelKey: 'filePermissions.print_watermark', icon: PrinterIcon },
    { key: 'share', labelKey: 'filePermissions.share', icon: UserGroupIcon },
  ];

  // Folder permission items
  const folderPermissionItems = [
    { key: 'create', labelKey: 'folderPermissions.create', icon: DocumentIcon },
    { key: 'copy', labelKey: 'folderPermissions.copy', icon: ClipboardDocumentIcon },
    { key: 'add', labelKey: 'folderPermissions.add', icon: DocumentIcon },
    { key: 'move', labelKey: 'folderPermissions.move', icon: ArrowPathIcon },
    { key: 'share', labelKey: 'folderPermissions.share', icon: UserGroupIcon },
    { key: 'delete', labelKey: 'folderPermissions.delete', icon: XMarkIcon },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('files')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm
              ${
                activeTab === 'files'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <div className="flex items-center space-x-2">
              <DocumentIcon className="w-5 h-5" />
              <span>{t('tabs.files')}</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('folders')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm
              ${
                activeTab === 'folders'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <div className="flex items-center space-x-2">
              <FolderIcon className="w-5 h-5" />
              <span>{t('tabs.folders')}</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="bg-white shadow rounded-lg p-6">
        {activeTab === 'files' ? (
          <div>
            <div className="flex items-center space-x-2 mb-6">
              <DocumentIcon className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">{t('filePermissions.title')}</h2>
            </div>
            
            <div className="space-y-4">
              {filePermissionItems.map(({ key, labelKey, icon: Icon }) => (
                <div key={key} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                  <div className="flex items-center space-x-3">
                    <Icon className="w-5 h-5 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">{t(labelKey)}</span>
                  </div>
                  <ToggleSwitch
                    checked={(() => {
                      const value = filePermissions[key as keyof FilePermissionVisibility];
                      // For all fields, use the value or default to true
                      return value === true;
                    })()}
                    onChange={(checked) => handleToggle(key, checked)}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center space-x-2 mb-6">
              <FolderIcon className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">{t('folderPermissions.title')}</h2>
            </div>
            
            <div className="space-y-4">
              {folderPermissionItems.map(({ key, labelKey, icon: Icon }) => (
                <div key={key} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                  <div className="flex items-center space-x-3">
                    <Icon className="w-5 h-5 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">{t(labelKey)}</span>
                  </div>
                  <ToggleSwitch
                    checked={folderPermissions[key as keyof FolderPermissionVisibility] === true}
                    onChange={(checked) => handleToggle(key, checked)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bulk Update Modal */}
      {showBulkUpdateModal && pendingChanges && (
        <BulkUpdateModal
          isOpen={showBulkUpdateModal}
          isEnabling={isEnabling}
          title={t('bulkModal.title')}
          descriptionEnabling={t('bulkModal.descriptionEnabling')}
          descriptionDisabling={t('bulkModal.descriptionDisabling')}
          applyToAll={t('bulkModal.applyToAll')}
          futureOnly={t('bulkModal.futureOnly')}
          cancel={t('bulkModal.cancel')}
          onClose={() => {
            setShowBulkUpdateModal(false);
            setPendingChanges(null);
            setIsEnabling(false);
          }}
          onConfirm={async (applyToExisting) => {
            // Apply the pending change to state first
            if (activeTab === 'files' && pendingChanges.file_permissions) {
              const key = Object.keys(pendingChanges.file_permissions)[0];
              const newValue = pendingChanges.file_permissions[key as keyof FilePermissionVisibility];
              const updatedPermissions = { ...filePermissions, [key]: newValue };
              setFilePermissions(updatedPermissions);
              // Update ref immediately so handleSave can use it
              filePermissionsRef.current = updatedPermissions;
              // Wait a tick to ensure state is updated
              await new Promise(resolve => setTimeout(resolve, 0));
            } else if (activeTab === 'folders' && pendingChanges.folder_permissions) {
              const key = Object.keys(pendingChanges.folder_permissions)[0];
              const newValue = pendingChanges.folder_permissions[key as keyof FolderPermissionVisibility];
              const updatedPermissions = { ...folderPermissions, [key]: newValue };
              setFolderPermissions(updatedPermissions);
              // Update ref immediately so handleSave can use it
              folderPermissionsRef.current = updatedPermissions;
              // Wait a tick to ensure state is updated
              await new Promise(resolve => setTimeout(resolve, 0));
            }
            // Then save
            await handleSave(applyToExisting);
          }}
        />
      )}
    </div>
  );
};

export default AdminPolicySettings;

